import React from 'react';

import { Field, useField } from 'formik';
import { FormControlLabel, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { jbColors } from 'theme/jbColors';
import theme from 'theme';
import { JBColors } from '@bjb-gcrm/gcrm-frontend-components';

export const useTextfieldStyles = makeStyles(() => ({
  labelRoot: {
    marginLeft: '0 !important',
    marginTop: '8px',
    marginRight: '0 !important',
    width: '100%',
    '& .MuiFormControlLabel-label': {
      color: JBColors.support.greenSmoke[100],
    },
  },
  helperText: {
    fontWeight: 'normal',
    textAlign: 'right',
    color: 'red',
  },
  labelPlacementTop: {
    alignItems: 'flex-start !important',
  },
  textInput: {
    marginTop: theme.spacing(0.5),
    width: '100%',
    '& .MuiFilledInput-inputMarginDense': {
      paddingLeft: theme.spacing(1.5),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      width: '100%',
    },
    '& .MuiFilledInput-multiline.MuiFilledInput-marginDense': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiFilledInput-input': {
      backgroundColor: jbColors.support.greenSmoke[10],
      cursor: 'auto',
    },
    '& .MuiFormHelperText-contained': {
      marginLeft: 0,
    },
  },
}));

interface TextFieldProps {
  label: string;
  name: string;
  maxChar: number;
  minChar?: number;
  readOnly?: boolean;
  disabled?: boolean;
  customError?: any;
  placeholder?: string;
  showCharacterCount?: boolean;
  multiline?: boolean;
  rowCount?: number;
  propFormikContext?: any;
  customId?: string;
  customInputProps?: any;
  customType?: string;
  showLengthInfo?: boolean;
}

const ValidatedTextField: React.FC<TextFieldProps> = ({
  rowCount = 1,
  showCharacterCount = false,
  ...props
}: TextFieldProps) => {
  const { label } = props;
  const classes = useTextfieldStyles();
  const [field, meta] = useField(props);
  if (!field.value) {
    field.value = '';
  }

  let errorText = meta.error && meta.touched ? meta.error : '';
  if (props.customError) {
    errorText = props.customError;
  }

  return (
    <FormControlLabel
      label={label}
      labelPlacement="top"
      disabled={props.disabled}
      classes={{
        root: classes.labelRoot,
        labelPlacementTop: classes.labelPlacementTop,
      }}
      control={
        <Field
          sx={{ marginTop: '3px' }}
          variant="filled"
          margin="dense"
          className={classes.textInput}
          rows={rowCount}
          type={props.customType ?? 'text'}
          multiline={rowCount !== 1 || props.multiline}
          id={props.customId ?? `${props.name}-text-input`}
          data-testid={`${props.name}-text-input`}
          as={TextField}
          inputProps={{
            maxLength: props.maxChar,
            placeholder: props.placeholder,
            readOnly: props.readOnly,
            disabled: props.disabled,
          }}
          helperText={
            errorText ? (
              <div style={{ whiteSpace: 'pre-wrap' }}>{errorText}</div>
            ) : (
              showCharacterCount &&
              (field.value ? `${field.value.toLocaleString().length}/${props.maxChar}` : `0/${props.maxChar}`)
            )
          }
          errortext={errorText}
          FormHelperTextProps={{
            style:
              showCharacterCount && !errorText
                ? {
                    textAlign: 'right',
                    fontWeight: 'normal',
                  }
                : undefined,
          }}
          InputProps={props.customInputProps ?? { disableUnderline: true }}
          {...field}
          error={!!errorText}
          fullWidth
        />
      }
    />
  );
};

export default ValidatedTextField;
